/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, April 17th 2023, 10:08:00 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { ALLOW_MULTI_ORGANIZATION_FOR_ON_PREMISE } from "config";
import { Organization } from "lib/schemas";
import { PackageWidgets } from "saas-subscription/PackageWidgets";
import { SubscriptionTracker } from "saas-subscription/schemas";
import { useSaaSSubscriptionAPI } from "saas-subscription/services/SubscriptionAPI";

const canAccessEnterpriseWithoutSubscription = (organization: Organization) => {
  return organization?.is_main_organization || ALLOW_MULTI_ORGANIZATION_FOR_ON_PREMISE;
};

interface ISubscriptionContext {
  subscriptionTracker: SubscriptionTracker;
  subscriptionPlanName: string;
  isFetchingSubscriptionData: boolean;
}

const SubscriptionContext = React.createContext<ISubscriptionContext>({
  subscriptionTracker: null,
  subscriptionPlanName: null,
  isFetchingSubscriptionData: false,
});

type SubscriptionProviderProps = React.PropsWithChildren<{
  organization: Organization;
}>;

export const SubscriptionProvider = ({ children, organization }: SubscriptionProviderProps) => {
  const mainOrganizationTracker: SubscriptionTracker = React.useMemo(
    () =>
      canAccessEnterpriseWithoutSubscription(organization)
        ? {
            product_code: "onestop-enterprise",
            completed_checkout_session_id: null,
            organization_id: organization?.id,
            status: "active",
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization?.id, organization?.is_main_organization]
  );

  const [subscriptionTracker, setSubscriptionTracker] = React.useState<SubscriptionTracker>(mainOrganizationTracker);
  const subscriptionPlanName = PackageWidgets[subscriptionTracker?.product_code]?.shortName;

  const { useSubscriptionTrackerAPI } = useSaaSSubscriptionAPI();

  const {
    fetchSubscriptionTracker,
    isMakingAPIRequest: isFetchingSubscriptionData,
    isSuccessfulAPIRequest,
  } = useSubscriptionTrackerAPI();

  React.useEffect(() => {
    if (organization?.id) {
      fetchSubscriptionTracker(organization.id, setSubscriptionTracker);
    }
  }, [organization?.id, fetchSubscriptionTracker]);

  React.useEffect(() => {
    if (!isSuccessfulAPIRequest) setSubscriptionTracker(mainOrganizationTracker);
  }, [isSuccessfulAPIRequest, mainOrganizationTracker]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptionTracker,
        subscriptionPlanName,
        isFetchingSubscriptionData,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = () => React.useContext(SubscriptionContext);
